import type { Board, GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../jsxgraph-theme.service';
import {
  createDefaultAxes,
  DEFAULT_AXIS_LABEL_OFFSET_HORIZONTAL,
  DEFAULT_AXIS_LABEL_OFFSET_VERTICAL,
  DEFAULT_BOUNDING_BOX,
  DEFAULT_GRID_CONFIG,
  DEFAULT_TICK_LABEL_OFFSET_SECONDARY_HORIZONTAL,
  DEFAULT_TICK_LABEL_OFFSET_X_AXIS,
  DEFAULT_TICK_LABEL_OFFSET_Y_AXIS,
} from './coordinate-system.defaults';
import {
  boundingBoxToTuple,
  type AxisConfig,
  type AxisLabelConfig,
  type AxisOrientation,
  type BoundingBoxTuple,
  type CoordinateSystemConfig,
  type GridConfig,
} from './coordinate-system.model';

export interface CoordinateSystemCreatorOptions {
  board: Board;
  themeService: JsxgraphThemeService;
  config?: CoordinateSystemConfig;
}

export interface CoordinateSystemResult {
  objects: Map<string, GeometryElement>;
}

// =============================================================================
// LABEL FORMATTERS
// =============================================================================

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : Math.abs(a);
}

/**
 * Format a value as a multiple of π.
 * E.g., π/2 → "π/2", π → "π", 3π/2 → "3π/2", 2π → "2π"
 */
function formatPiLabel(value: number, step: number): string {
  const den = Math.round(Math.PI / step);
  const k = Math.round(value / step);

  if (Math.abs(value - k * step) > 1e-8) {
    return '';
  }
  if (k === 0) {
    return '0';
  }

  const g = gcd(k, den);
  const n = k / g;
  const d = den / g;
  const sign = n < 0 ? '−' : '';
  const an = Math.abs(n);

  if (d === 1) {
    return sign + (an === 1 ? 'π' : `${an}π`);
  }
  return sign + (an === 1 ? `π/${d}` : `${an}π/${d}`);
}

/**
 * Format a value (in radians) as degrees.
 * E.g., π/2 → "90°", π → "180°"
 */
function formatDegreesLabel(value: number, step: number): string {
  const k = Math.round(value / step);
  if (Math.abs(value - k * step) > 1e-8) {
    return '';
  }
  return `${Math.round((value * 180) / Math.PI)}°`;
}

// =============================================================================
// HELPERS
// =============================================================================

function createAxisLabel(
  board: Board,
  orientation: AxisOrientation,
  config: AxisLabelConfig,
  boundingbox: BoundingBoxTuple,
  theme: ReturnType<JsxgraphThemeService['getTheme']>,
  axisPosition: number,
): GeometryElement {
  const [, top, right] = boundingbox;
  const [x, y] = orientation === 'horizontal' ? [right, axisPosition] : [axisPosition, top];
  const defaultOffset =
    orientation === 'horizontal' ? DEFAULT_AXIS_LABEL_OFFSET_HORIZONTAL : DEFAULT_AXIS_LABEL_OFFSET_VERTICAL;

  return board.create('text', [x, y, config.text], {
    anchorX: orientation === 'horizontal' ? 'right' : 'left',
    anchorY: orientation === 'horizontal' ? 'bottom' : 'top',
    offset: config.offset ?? defaultOffset,
    fontSize: config.fontSize ?? theme.typography.fontSize.base,
    cssStyle: `font-family: ${theme.typography.fontFamily}; font-style: italic;`,
    fixed: true,
    highlight: false,
  }) as GeometryElement;
}

/**
 * Gets the tick distance for a primary axis of a given orientation.
 * Used to determine grid spacing for dot grids.
 */
function getPrimaryAxisTickDistance(axes: AxisConfig[], orientation: AxisOrientation): number {
  const primaryAxis = axes.find((a) => a.orientation === orientation && a.isPrimary !== false);
  return primaryAxis?.ticks?.ticksDistance ?? 1;
}

// =============================================================================
// MAIN CREATOR
// =============================================================================

export function createCoordinateSystem(opts: CoordinateSystemCreatorOptions): CoordinateSystemResult {
  const { board, themeService, config } = opts;
  const objects = new Map<string, GeometryElement>();

  if (!board) {
    return { objects };
  }

  const theme = themeService.getTheme();
  const bbox: BoundingBoxTuple = config?.boundingbox
    ? boundingBoxToTuple(config.boundingbox)
    : (board.getBoundingBox() as BoundingBoxTuple | null) ?? boundingBoxToTuple(DEFAULT_BOUNDING_BOX);

  // Grid config
  const gridConfig: GridConfig | false =
    config?.grid === true ? DEFAULT_GRID_CONFIG : config?.grid === false ? false : config?.grid ?? false;
  // Grid is enabled if config exists and visible is not explicitly false
  const gridEnabled = gridConfig !== false && gridConfig.visible !== false;
  const gridType = gridConfig && gridConfig.type ? gridConfig.type : 'lines';

  // Axes config - use default if not specified or true, empty array if false
  const axesConfig: AxisConfig[] =
    config?.axes === false ? [] : config?.axes === true || !config?.axes ? createDefaultAxes() : config.axes;

  // Determine primary axes (first of each orientation, or explicitly marked)
  const processedAxes = axesConfig.map((axis) => {
    const sameOrientationAxes = axesConfig.filter((a) => a.orientation === axis.orientation);
    const isFirstOfOrientation = sameOrientationAxes[0] === axis;
    const isPrimary = axis.isPrimary ?? isFirstOfOrientation;
    const drawGridLines = axis.drawGridLines ?? isPrimary;

    return { ...axis, isPrimary, drawGridLines };
  });

  // Create dot grid if enabled (using primary axis tick distances)
  if (gridEnabled && gridType === 'dots') {
    const [left, top, right, bottom] = bbox;
    const xStep = getPrimaryAxisTickDistance(processedAxes, 'horizontal');
    const yStep = getPrimaryAxisTickDistance(processedAxes, 'vertical');

    for (let x = Math.ceil(left / xStep) * xStep; x <= Math.floor(right / xStep) * xStep; x += xStep) {
      for (let y = Math.ceil(bottom / yStep) * yStep; y <= Math.floor(top / yStep) * yStep; y += yStep) {
        const dot = board.create('point', [x, y], {
          fixed: true,
          showInfobox: false,
          withLabel: false,
          size: 0.5,
          strokeColor: theme.colors.grid,
          fillColor: theme.colors.grid,
          highlight: false,
          layer: 0, // Render behind all other elements
        }) as GeometryElement;
        objects.set(`grid-dot-${x}-${y}`, dot);
      }
    }
  }

  // Create axes
  const baseAxisOptions = {
    strokeColor: theme.colors.axis,
    strokeWidth: 1,
    highlight: false,
    straightFirst: true,
    straightLast: true,
  };

  for (const axisConfig of processedAxes) {
    const { id, orientation, position = 0, isPrimary, drawGridLines, ticks: ticksConfig, axisLabel } = axisConfig;

    const isVertical = orientation === 'vertical';
    const shouldDrawGridLines = isPrimary && drawGridLines && gridEnabled && gridType === 'lines';

    // Determine axis points based on orientation and position
    const points: [[number, number], [number, number]] =
      orientation === 'horizontal'
        ? [
            [0, position],
            [1, position],
          ]
        : [
            [position, 0],
            [position, 1],
          ];

    // Build generateLabelText function if needed for π or degrees formatting
    let generateLabelText: ((tick: unknown, zero: unknown, value?: number | string | null) => string) | undefined;
    if (ticksConfig?.labelFormat === 'pi' || ticksConfig?.labelFormat === 'degrees') {
      const step = ticksConfig.ticksDistance ?? Math.PI / 2;
      // For horizontal axis use x-coordinate (index 1), for vertical use y-coordinate (index 2)
      const coordIndex = isVertical ? 2 : 1;
      generateLabelText = (tick, _zero, value) => {
        let tickVal: number;
        if (typeof value === 'number' && !isNaN(value)) {
          tickVal = value;
        } else {
          // Extract coordinate from tick object when value is not provided
          const t = tick as { usrCoords?: number[] };
          tickVal = t?.usrCoords ? t.usrCoords[coordIndex] : 0;
        }
        return ticksConfig.labelFormat === 'pi' ? formatPiLabel(tickVal, step) : formatDegreesLabel(tickVal, step);
      };
    }

    // Determine tick label offset based on axis orientation and primary status
    const defaultLabelOffset = isVertical
      ? DEFAULT_TICK_LABEL_OFFSET_Y_AXIS
      : !isPrimary
        ? DEFAULT_TICK_LABEL_OFFSET_SECONDARY_HORIZONTAL
        : DEFAULT_TICK_LABEL_OFFSET_X_AXIS;

    // Build ticks options - all configuration goes directly to JSXGraph's native ticks
    const ticksOptions: Record<string, unknown> = {
      strokeColor: shouldDrawGridLines ? theme.colors.grid : theme.colors.axis,
      strokeWidth: 1,
      majorHeight: shouldDrawGridLines ? -1 : ticksConfig?.majorHeight ?? 5,
      minorHeight: ticksConfig?.minorHeight ?? 0,
      minorTicks: ticksConfig?.minorTicks ?? 0,
      drawLabels: ticksConfig?.drawLabels ?? true,
      drawZero: ticksConfig?.drawZero ?? false,
      ticksDistance: ticksConfig?.ticksDistance,
      insertTicks: ticksConfig?.ticksDistance === undefined,
      strokeOpacity: isPrimary ? 1 : 0,
      label: {
        fontSize: ticksConfig?.label?.fontSize ?? theme.typography.fontSize.small,
        cssStyle: `font-family: ${theme.typography.fontFamily}; color: ${theme.colors.textSecondary};`,
        offset: ticksConfig?.label?.offset ?? defaultLabelOffset,
        anchorX: isVertical ? 'left' : 'middle',
        anchorY: isVertical ? 'middle' : 'top',
      },
    };

    // Add optional JSXGraph tick options if defined
    if (ticksConfig?.scale !== undefined) {
      ticksOptions['scale'] = ticksConfig.scale;
    }
    if (ticksConfig?.scaleSymbol !== undefined) {
      ticksOptions['scaleSymbol'] = ticksConfig.scaleSymbol;
    }
    if (ticksConfig?.fixedTicks !== undefined) {
      ticksOptions['fixedTicks'] = ticksConfig.fixedTicks;
    }
    if (ticksConfig?.labels !== undefined) {
      ticksOptions['labels'] = ticksConfig.labels;
    }
    if (generateLabelText) {
      ticksOptions['generateLabelText'] = generateLabelText;
    }

    const axis = board.create('axis', points, {
      ...baseAxisOptions,
      name: id,
      withLabel: false,
      ticks: ticksOptions,
    }) as GeometryElement;
    objects.set(id, axis);

    // Create axis label if configured
    if (axisLabel) {
      objects.set(`${id}-label`, createAxisLabel(board, orientation, axisLabel, bbox, theme, position));
    }
  }

  return { objects };
}
